// Game data (PRD section 17, "Datos en JSON"): items, recipes, machines, terrains, vehicles, orders
// and the prototype's tiles. Balancing the game is editing `src/data/sim/*.json`; this module
// checks those files when they load and links every reference into typed definitions.
import { TERRAIN, type Island, type TerrainCode } from '../world/island';
import { inBounds, keyOf, portEdge, type Cell, type Side, type Socket } from './geometry';
import { bool, cell, DataError, fail, int, list, nonNegative, oneOf, positive, record, ref, side, text } from './validate';

export type ItemId = string;
/** Amounts per item: stock contents, recipe ingredients or flows per minute. */
export type Amounts = ReadonlyMap<ItemId, number>;

export interface ItemDef {
  readonly id: ItemId;
  readonly name: string;
  /** Swatch for figures, from the palette of PRD section 14.4. */
  readonly color: string;
}

export interface RecipeDef {
  readonly id: string;
  readonly name: string;
  /** Seconds one craft takes in a machine of speed 1. */
  readonly seconds: number;
  readonly in: Amounts;
  readonly out: Amounts;
}

export interface MachineDef {
  readonly id: string;
  readonly name: string;
  readonly recipe: RecipeDef;
  /** Footprint in cells before rotation (PRD section 6: machines are 2 × 2 blocks). */
  readonly size: Cell;
  readonly speed: number;
  /** Where material enters and leaves, in footprint coordinates before rotation. */
  readonly in: Socket;
  readonly out: Socket;
  /** Crafts per minute at full speed. */
  readonly craftsPerMinute: number;
}

export interface BeltDef {
  readonly name: string;
  /** Items per minute one belt cell carries at most. */
  readonly rate: number;
}

export interface ResourceDef {
  readonly item: ItemDef;
  /** Items per minute a resource tile on this terrain extracts (resources are infinite, E6). */
  readonly rate: number;
}

export interface TerrainDef {
  readonly code: TerrainCode;
  readonly name: string;
  readonly buildable: boolean;
  /** Interior size in cells, where it is decided (PRD section 5 leaves most sizes open). */
  readonly interior?: Cell;
  readonly resource?: ResourceDef;
}

export interface VehicleDef {
  readonly id: string;
  readonly name: string;
  /** Items carried per trip. */
  readonly capacity: number;
  /** Tiles travelled per minute. */
  readonly speed: number;
  /** Seconds spent loading and unloading on each round trip. */
  readonly handlingSeconds: number;
}

export interface OrderDef {
  readonly id: string;
  readonly name: string;
  readonly item: ItemDef;
  readonly amount: number;
  /** Coins the colony pays when the order is fulfilled. */
  readonly reward: number;
}

export type TileKind = 'resource' | 'factory' | 'warehouse';
export type PortKind = 'in' | 'out';

export interface PortDef {
  readonly id: string;
  readonly kind: PortKind;
  readonly side: Side;
  /** Interior cell the port sits next to, for tiles with an interior. */
  readonly edge?: Socket;
}

export interface TileDef {
  readonly id: string;
  readonly name: string;
  readonly kind: TileKind;
  /** Position on the island map (column, row). */
  readonly cell: Cell;
  readonly terrain: TerrainDef;
  readonly ports: readonly PortDef[];
  /** Interior size of a factory tile. */
  readonly interior?: Cell;
  /** What a resource tile extracts. */
  readonly resource?: ResourceDef;
}

export interface GameData {
  readonly items: ReadonlyMap<ItemId, ItemDef>;
  readonly recipes: ReadonlyMap<string, RecipeDef>;
  readonly machines: ReadonlyMap<string, MachineDef>;
  readonly belt: BeltDef;
  readonly terrains: ReadonlyMap<TerrainCode, TerrainDef>;
  readonly vehicles: ReadonlyMap<string, VehicleDef>;
  readonly orders: ReadonlyMap<string, OrderDef>;
  /** The prototype's world: its name, playable tiles, starting coins and orders. */
  readonly world: {
    readonly name: string;
    readonly tiles: ReadonlyMap<string, TileDef>;
    /** Items each port of a resource or factory tile holds at most. Warehouses have no limit. */
    readonly portCapacity: number;
    readonly coins: number;
    readonly orders: readonly OrderDef[];
  };
}

/** The raw JSON files, as imported. */
export interface RawGameData {
  readonly items: unknown;
  readonly recipes: unknown;
  readonly machines: unknown;
  readonly terrains: unknown;
  readonly vehicles: unknown;
  readonly orders: unknown;
  readonly prototype: unknown;
}

function table<T>(raw: unknown, path: string, read: (value: Record<string, unknown>, id: string, path: string) => T): Map<string, T> {
  const out = new Map<string, T>();
  for (const [id, value] of Object.entries(record(raw, path))) {
    if (!/^[a-z0-9_-]+$/.test(id)) fail(`${path}.${id}`, 'el identificador solo admite minúsculas, cifras, «_» y «-»');
    out.set(id, read(record(value, `${path}.${id}`), id, `${path}.${id}`));
  }
  if (out.size === 0) fail(path, 'no puede estar vacío');
  return out;
}

function amounts(raw: unknown, items: ReadonlyMap<ItemId, ItemDef>, path: string): Amounts {
  const out = new Map<ItemId, number>();
  for (const [id, n] of Object.entries(record(raw, path))) {
    ref(items, id, `${path}.${id}`);
    out.set(id, positive(n, `${path}.${id}`));
  }
  if (out.size === 0) fail(path, 'no puede estar vacío');
  return out;
}

function socket(raw: unknown, size: Cell, path: string): Socket {
  const value = record(raw, path);
  const at = cell(value.cell, `${path}.cell`);
  if (!inBounds(at, size)) fail(`${path}.cell`, 'debe estar dentro de la máquina');
  return { cell: at, side: side(value.side, `${path}.side`) };
}

/** Validates the game data files and links their references. Throws a DataError naming the first problem. */
export function parseGameData(raw: RawGameData, island: Island): GameData {
  try {
    return parse(raw, island);
  } catch (error) {
    if (error instanceof DataError) throw new DataError(`Datos no válidos: ${error.message}`);
    throw error;
  }
}

function parse(raw: RawGameData, island: Island): GameData {
  const items = table(raw.items, 'items', (v, id, p): ItemDef => {
    const color = text(v.color, `${p}.color`);
    if (!/^#[0-9a-f]{6}$/i.test(color)) fail(`${p}.color`, 'debe ser un color #rrggbb');
    return { id, name: text(v.name, `${p}.name`), color };
  });

  const recipes = table(raw.recipes, 'recipes', (v, id, p): RecipeDef => ({
    id,
    name: text(v.name, `${p}.name`),
    seconds: positive(v.seconds, `${p}.seconds`),
    in: amounts(v.in, items, `${p}.in`),
    out: amounts(v.out, items, `${p}.out`),
  }));

  const machinesRaw = record(raw.machines, 'machines');
  const machines = table(machinesRaw.machines, 'machines.machines', (v, id, p): MachineDef => {
    const recipe = ref(recipes, v.recipe, `${p}.recipe`);
    const size = cell(v.size, `${p}.size`);
    if (size[0] < 1 || size[1] < 1) fail(`${p}.size`, 'debe medir al menos 1 × 1');
    const speed = positive(v.speed, `${p}.speed`);
    return {
      id,
      name: text(v.name, `${p}.name`),
      recipe,
      size,
      speed,
      in: socket(v.in, size, `${p}.in`),
      out: socket(v.out, size, `${p}.out`),
      craftsPerMinute: (60 / recipe.seconds) * speed,
    };
  });
  const beltRaw = record(machinesRaw.belt, 'machines.belt');
  const belt: BeltDef = { name: text(beltRaw.name, 'machines.belt.name'), rate: positive(beltRaw.rate, 'machines.belt.rate') };

  const terrainsRaw = record(raw.terrains, 'terrains');
  const terrains = new Map<TerrainCode, TerrainDef>();
  for (const code of Object.keys(TERRAIN) as TerrainCode[]) {
    const p = `terrains.${code}`;
    const v = record(terrainsRaw[code], p);
    const interior = v.interior === undefined ? undefined : cell(v.interior, `${p}.interior`);
    if (interior && (interior[0] < 2 || interior[1] < 2)) fail(`${p}.interior`, 'debe medir al menos 2 × 2');
    let resource: ResourceDef | undefined;
    if (v.resource !== undefined) {
      const r = record(v.resource, `${p}.resource`);
      resource = { item: ref(items, r.item, `${p}.resource.item`), rate: positive(r.rate, `${p}.resource.rate`) };
    }
    terrains.set(code, {
      code,
      name: TERRAIN[code].name,
      buildable: bool(v.buildable, `${p}.buildable`),
      ...(interior ? { interior } : {}),
      ...(resource ? { resource } : {}),
    });
  }
  for (const code of Object.keys(terrainsRaw)) if (!(code in TERRAIN)) fail(`terrains.${code}`, 'no es un terreno de la isla');

  const vehicles = table(raw.vehicles, 'vehicles', (v, id, p): VehicleDef => ({
    id,
    name: text(v.name, `${p}.name`),
    capacity: positive(v.capacity, `${p}.capacity`),
    speed: positive(v.speed, `${p}.speed`),
    handlingSeconds: nonNegative(v.handlingSeconds, `${p}.handlingSeconds`),
  }));

  const orders = table(raw.orders, 'orders', (v, id, p): OrderDef => ({
    id,
    name: text(v.name, `${p}.name`),
    item: ref(items, v.item, `${p}.item`),
    amount: positive(v.amount, `${p}.amount`),
    reward: nonNegative(v.reward, `${p}.reward`),
  }));

  return { items, recipes, machines, belt, terrains, vehicles, orders, world: parseWorld(raw.prototype, island, terrains, orders) };
}

function parseWorld(raw: unknown, island: Island, terrains: ReadonlyMap<TerrainCode, TerrainDef>, orders: ReadonlyMap<string, OrderDef>): GameData['world'] {
  const v = record(raw, 'prototype');
  const tiles = new Map<string, TileDef>();
  const usedCells = new Set<string>();
  list(v.tiles, 'prototype.tiles').forEach((t, i) => {
    const p = `prototype.tiles[${i}]`;
    const tile = parseTile(record(t, p), p, island, terrains);
    if (tiles.has(tile.id)) fail(`${p}.id`, `«${tile.id}» está repetido`);
    if (usedCells.has(keyOf(tile.cell))) fail(`${p}.cell`, 'ya la ocupa otra casilla');
    usedCells.add(keyOf(tile.cell));
    tiles.set(tile.id, tile);
  });
  if (tiles.size === 0) fail('prototype.tiles', 'no puede estar vacío');
  const active = list(v.orders, 'prototype.orders').map((o, i) => ref(orders, o, `prototype.orders[${i}]`));
  if (new Set(active).size !== active.length) fail('prototype.orders', 'tiene encargos repetidos');
  return {
    name: text(v.name, 'prototype.name'),
    tiles,
    portCapacity: positive(v.portCapacity, 'prototype.portCapacity'),
    coins: nonNegative(v.coins, 'prototype.coins'),
    orders: active,
  };
}

const TILE_KINDS: readonly TileKind[] = ['resource', 'factory', 'warehouse'];

function parseTile(v: Record<string, unknown>, p: string, island: Island, terrains: ReadonlyMap<TerrainCode, TerrainDef>): TileDef {
  const id = text(v.id, `${p}.id`);
  const kind = oneOf(v.kind, TILE_KINDS, `${p}.kind`);
  const at = cell(v.cell, `${p}.cell`);
  const code = island.terrain.get(keyOf(at));
  if (code === undefined) fail(`${p}.cell`, 'no es una casilla de la isla');
  const terrain = terrains.get(code);
  if (!terrain) fail(`${p}.cell`, `el terreno «${code}» no tiene datos`);
  if (!terrain.buildable) fail(`${p}.cell`, `no se puede ocupar una casilla de ${terrain.name.toLowerCase()}`);
  const interior = kind === 'factory' ? terrain.interior : undefined;
  if (kind === 'factory' && !interior) fail(`${p}.cell`, `el terreno ${terrain.name.toLowerCase()} aún no tiene tamaño interior`);
  const resource = kind === 'resource' ? terrain.resource : undefined;
  if (kind === 'resource' && !resource) fail(`${p}.cell`, `el terreno ${terrain.name.toLowerCase()} no tiene recurso`);

  const ports = list(v.ports, `${p}.ports`).map((raw, j): PortDef => {
    const pp = `${p}.ports[${j}]`;
    const port = record(raw, pp);
    const portSide = side(port.side, `${pp}.side`);
    const def = { id: text(port.id, `${pp}.id`), kind: oneOf(port.kind, ['in', 'out'] as const, `${pp}.kind`), side: portSide };
    if (!interior) return def;
    const index = int(port.index, `${pp}.index`);
    const length = portSide === 'N' || portSide === 'S' ? interior[0] : interior[1];
    if (index < 0 || index >= length) fail(`${pp}.index`, `debe estar entre 0 y ${length - 1}`);
    return { ...def, edge: portEdge(portSide, index, interior) };
  });
  const ids = new Set(ports.map((port) => port.id));
  if (ids.size !== ports.length) fail(`${p}.ports`, 'tiene puertas repetidas');
  const edges = new Set(ports.flatMap((port) => (port.edge ? [`${keyOf(port.edge.cell)}${port.edge.side}`] : [])));
  if (edges.size !== ports.filter((port) => port.edge).length) fail(`${p}.ports`, 'dos puertas ocupan el mismo sitio del borde');
  const needs: Record<TileKind, PortKind[]> = { resource: ['out'], factory: ['in', 'out'], warehouse: ['in'] };
  for (const portKind of needs[kind]) {
    if (!ports.some((port) => port.kind === portKind)) fail(`${p}.ports`, `necesita al menos una puerta de ${portKind === 'in' ? 'entrada' : 'salida'}`);
  }
  if (kind === 'resource' && (ports.length !== 1 || ports[0]?.kind !== 'out')) fail(`${p}.ports`, 'una casilla de recurso tiene una sola puerta, de salida');
  if (kind === 'warehouse' && ports.some((port) => port.kind === 'out')) fail(`${p}.ports`, 'el almacén solo tiene entradas');

  return {
    id,
    name: text(v.name, `${p}.name`),
    kind,
    cell: at,
    terrain,
    ports,
    ...(interior ? { interior } : {}),
    ...(resource ? { resource } : {}),
  };
}
